import { useState } from 'react';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import * as styles from '../styles/components/import-data-modal.css.ts';
import logger from '../utils/logger';
import { selectFile } from '../services/fileService';
import { parseCSVToDataset } from '../services/csvProcessor';
import { useProjectStore } from '../stores/projectStore';
import { DataSet, createTabId, createPaneId, LayoutState } from '../../types/index';
import type { PaneNode } from '../../types/pane.types';

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PreviewData {
  file: File;
  dataset: DataSet;
  warnings: string[];
}

export function ImportDataModal({ isOpen, onClose }: ImportDataModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const projectStore = useProjectStore();
  const getActiveContainer = useProjectStore((state) => state.getActiveContainer);
  const getCurrentWorkspace = useProjectStore((state) => state.getCurrentWorkspace);
  const addTab = useProjectStore((state) => state.addTab);

  const handleFileSelect = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setPreviewData(null);

      // ファイル選択ダイアログを表示
      const file = await selectFile('.csv,.tsv', false) as File | null;

      if (!file) {
        // ユーザーがキャンセル
        setIsLoading(false);
        return;
      }

      logger.info('[ImportDataModal] File selected:', file.name);

      // CSVファイルを処理してプレビュー
      const { dataset, warnings } = await parseCSVToDataset(file);

      if (!dataset) {
        throw new Error('Failed to process CSV file');
      }

      // プレビューデータを設定
      setPreviewData({ file, dataset, warnings });

    } catch (err) {
      logger.error('[ImportDataModal] File processing failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = () => {
    if (!previewData) return;

    try {
      // データセットをプロジェクトに追加
      projectStore.addDataset(previewData.dataset as DataSet);

      logger.info('[ImportDataModal] Dataset imported successfully:', {
        datasetId: previewData.dataset.id,
        datasetName: previewData.dataset.name,
        rowCount: previewData.dataset.rowCount
      });

      // DataTableタブを作成して開く
      // アクティブコンテナを取得
      const activeContainer = getActiveContainer();
      let targetContainerId = activeContainer?.id;

      if (!targetContainerId) {
        // アクティブコンテナがない場合は、rootPaneから最初のコンテナを探す
        const layout = getCurrentWorkspace()?.layoutState as LayoutState | undefined;
        const findFirstContainer = (node: PaneNode): string | undefined => {
          if (node.type === 'container') {
            return node.id;
          }
          if (node.type === 'split') {
            return findFirstContainer(node.leftChild) || findFirstContainer(node.rightChild);
          }
          return undefined;
        };
        const containerId = layout?.rootPane ? findFirstContainer(layout.rootPane) : undefined;
        targetContainerId = containerId ? createPaneId(containerId) : undefined;
      }

      logger.info('[ImportDataModal] Target container ID:', targetContainerId);

      if (targetContainerId) {
        const datasetId = previewData.dataset.id;
        const datasetName = previewData.dataset.name;

        logger.info('[ImportDataModal] Creating tab with:', {
          datasetId,
          datasetName,
          targetContainerId
        });

        const newTab = {
          id: createTabId(`data-table-${datasetId}`),
          type: 'data-table' as const,
          title: datasetName,  // "Data Table: "プレフィックスは自動で追加される
          isClosable: true,
          metadata: {
            datasetId: datasetId
          }
        };

        addTab(targetContainerId, newTab, { activatePane: true });

        logger.info('[ImportDataModal] Created DataTable tab for imported dataset:', {
          tabId: newTab.id,
          datasetId: previewData.dataset.id,
          containerId: targetContainerId
        });
      } else {
        logger.error('[ImportDataModal] No container found to add tab');
      }

      // モーダルを閉じる
      onClose();
      // モーダルが閉じた後でステートをクリア
      setTimeout(() => {
        setPreviewData(null);
        setError(null);
      }, 300);
    } catch (err) {
      logger.error('[ImportDataModal] Import failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to import data');
    }
  };

  const handleCancel = () => {
    // クリア処理はonCloseの後で
    onClose();
    // モーダルが閉じた後でステートをクリア
    setTimeout(() => {
      setPreviewData(null);
      setError(null);
    }, 300);
  };

  const footer = previewData ? (
    <div className={styles.footerButtons}>
      <Button
        variant="primary"
        onClick={handleConfirmImport}
      >
        Import
      </Button>
      <Button
        variant="secondary"
        onClick={handleCancel}
      >
        Cancel
      </Button>
    </div>
  ) : null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Import Data"
      width="md"
      footer={footer}
    >
      <div className={styles.modalContent}>
        {!previewData ? (
          // ファイル選択画面
          <div className={styles.fileDropArea}>
            <button
              className={styles.openFileButton}
              onClick={handleFileSelect}
              disabled={isLoading}
            >
              <svg className={styles.fileIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 13H15M12 10V16M21 7V17C21 19.2091 19.2091 21 17 21H7C4.79086 21 3 19.2091 3 17V7C3 4.79086 4.79086 3 7 3H17C19.2091 3 21 4.79086 21 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className={styles.fileButtonTitle}>
                {isLoading ? 'Processing...' : 'Select File'}
              </span>
              <span className={styles.fileButtonDescription}>
                Click to browse for CSV or TSV files
              </span>
            </button>

            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <div className={styles.supportedFormats}>
              <span className={styles.formatLabel}>Supported formats:</span>
              <span className={styles.formatBadge}>CSV</span>
              <span className={styles.formatBadge}>TSV</span>
            </div>
          </div>
        ) : (
          // プレビュー画面
          <div className={styles.previewContainer}>
            <div className={styles.fileInfo}>
              <div className={styles.fileName}>{previewData.file.name}</div>
              <div className={styles.fileStats}>
                <span>Rows: {(previewData.dataset.rowCount || 0).toLocaleString()}</span>
                <span>•</span>
                <span>Columns: {previewData.dataset.schema.columns?.length || 0}</span>
                <span>•</span>
                <span>Size: {(previewData.file.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>

            {previewData.warnings.length > 0 && (
              <div className={styles.warningBox}>
                <div className={styles.warningTitle}>⚠️ Warnings</div>
                <ul className={styles.warningList}>
                  {previewData.warnings.slice(0, 3).map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                  {previewData.warnings.length > 3 && (
                    <li>... and {previewData.warnings.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}

            <div className={styles.dataPreview}>
              <div className={styles.previewTitle}>Data Preview</div>
              <table className={styles.previewTable}>
                <thead>
                  <tr>
                    {previewData.dataset.schema.columns?.slice(0, 5).map((column) => (
                      <th key={column.id}>{column.name}</th>
                    ))}
                    {(previewData.dataset.schema.columns?.length || 0) > 5 && <th>...</th>}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.min(5, previewData.dataset.rowCount || 0) }).map((_, rowIndex) => (
                    <tr key={rowIndex}>
                      {previewData.dataset.schema.columns?.slice(0, 5).map((column) => {
                        // dataがundefinedの場合は空セルを表示
                        if (!previewData.dataset.data) {
                          return <td key={column.id}></td>;
                        }
                        const data = previewData.dataset.data[column.id];
                        const value = data?.[rowIndex];
                        return <td key={column.id}>{String(value ?? '')}</td>;
                      })}
                      {(previewData.dataset.schema.columns?.length || 0) > 5 && <td>...</td>}
                    </tr>
                  ))}
                  {(previewData.dataset.rowCount || 0) > 5 && (
                    <tr>
                      <td colSpan={Math.min(6, (previewData.dataset.schema.columns?.length || 0) + 1)} className={styles.moreRows}>
                        ... {(previewData.dataset.rowCount || 0) - 5} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}